
import logger from '../../app/logger';

/**
 * Utility functions for handling media permissions.
 */

/**
 * Permission states that can be returned by the Permissions API.
 */
export const PERMISSION_STATES = {
    GRANTED: 'granted',
    DENIED: 'denied',
    PROMPT: 'prompt'
};

/**
 * Checks the current permission status for audio and video.
 *
 * @returns {Promise<Object>} Object containing audio and video permission states
 */
export async function checkPermissionStatus() {
    const result = {
        audio: null,
        video: null,
        supported: false
    };

    try {
        if (typeof navigator === 'undefined' || typeof navigator.permissions?.query !== 'function') {
            return result;
        }

        result.supported = true;

        // Check audio permission
        try {
            const audioPermission = await navigator.permissions.query({ name: 'microphone' });

            result.audio = audioPermission.state;
        } catch {
            // Microphone permission might not be supported
            result.audio = null;
        }

        // Check video permission
        try {
            const videoPermission = await navigator.permissions.query({ name: 'camera' });

            result.video = videoPermission.state;
        } catch {
            // Camera permission might not be supported
            result.video = null;
        }

        return result;
    } catch {
        return result;
    }
}

/**
 * Attempts to force permission dialog to show by using various techniques.
 *
 * @param {Object} options - Options for permission request
 * @param {boolean} options.audio - Whether to request audio permission
 * @param {boolean} options.video - Whether to request video permission
 * @param {boolean} options.forceDialog - Whether to force the permission dialog
 * @returns {Promise<Object>} Result object with success status and guidance
 */
export async function requestMediaPermissions({ audio = true, video = true, forceDialog = true } = {}) {
    const result = {
        success: false,
        showGuidance: false,
        message: '',
        permissionStatus: null,
        dialogShown: false
    };

    try {
        // If forceDialog is true, always try to show the permission dialog
        if (forceDialog) {
            return await forcePermissionDialog({ audio,
                video });
        }

        // Original logic for non-forced requests
        const permissionStatus = await checkPermissionStatus();

        result.permissionStatus = permissionStatus;

        if (permissionStatus.supported) {
            const audioDenied = audio && permissionStatus.audio === PERMISSION_STATES.DENIED;
            const videoDenied = video && permissionStatus.video === PERMISSION_STATES.DENIED;

            if (audioDenied || videoDenied) {
                result.showGuidance = true;
                result.message = 'Permissions have been denied. Please enable them in your browser settings.';

                return result;
            }

            const audioGranted = !audio || permissionStatus.audio === PERMISSION_STATES.GRANTED;
            const videoGranted = !video || permissionStatus.video === PERMISSION_STATES.GRANTED;

            if (audioGranted && videoGranted) {
                result.success = true;
                result.message = 'Permissions already granted.';

                return result;
            }
        }

        const constraints = {};

        if (audio) {
            constraints.audio = true;
        }
        if (video) {
            constraints.video = true;
        }

        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }

        result.success = true;
        result.message = 'Permissions granted successfully.';

        return result;

    } catch (error) {
        result.showGuidance = true;
        result.message = getPermissionErrorMessage(error);

        return result;
    }
}

/**
 * Maps a getUserMedia error to the guidance message shown to the user.
 *
 * @param {Error} error - The error thrown by getUserMedia.
 * @returns {string}
 */
function getPermissionErrorMessage(error) {
    switch (error?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
        return 'Permission denied. Please enable camera and microphone access in your browser settings and refresh the page.';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
        return 'No camera or microphone found. Please connect your devices and try again.';
    case 'NotReadableError':
    case 'TrackStartError':
        return 'Your camera or microphone is already in use by another application. Close it and try again.';
    default:
        return 'Unable to access camera or microphone. Please check your browser settings.';
    }
}

/**
 * Returns true if retrying getUserMedia with different constraints could plausibly succeed. A denial, a missing
 * device or a device held by another application fails identically no matter what constraints are used, so retrying
 * those only produces duplicate errors in the log (and on some browsers repeated prompts) before failing anyway.
 *
 * @param {Error} error - The error thrown by getUserMedia.
 * @returns {boolean}
 */
function isConstraintError(error) {
    return error?.name === 'OverconstrainedError'
        || error?.name === 'ConstraintNotSatisfiedError'
        || error?.name === 'TypeError';
}

/**
 * Forces the permission dialog to appear using various techniques.
 *
 * @param {Object} options - Options for permission request
 * @param {boolean} options.audio - Whether to request audio permission
 * @param {boolean} options.video - Whether to request video permission
 * @returns {Promise<Object>} Result object
 */
async function forcePermissionDialog({ audio = true, video = true } = {}) {
    const result = {
        success: false,
        showGuidance: false,
        message: '',
        dialogShown: true
    };

    // Constraint variations, from the plain request to progressively more specific ones. These only help when the
    // device rejected the requested constraints; everything else stops at the first attempt.
    const constraintVariations = [

        // Standard constraints
        {
            audio: Boolean(audio),
            video: Boolean(video)
        },

        // With specific device constraints
        {
            audio: audio ? { echoCancellation: true } : false,
            video: video ? { width: 640,
                height: 480 } : false
        },

        // With different quality settings
        {
            audio: audio ? { sampleRate: 44100 } : false,
            video: video ? { width: { ideal: 1280 },
                height: { ideal: 720 } } : false
        }
    ];
    let lastError;

    for (const constraints of constraintVariations) {
        try {
            const stream = await navigator.mediaDevices.getUserMedia(constraints);

            if (stream) {
                // Close the stream immediately
                stream.getTracks().forEach(track => track.stop());

                result.success = true;
                result.message = 'Permissions granted successfully.';

                return result;
            }
        } catch (error) {
            lastError = error;

            if (!isConstraintError(error)) {
                break;
            }

            logger.warn(`Media permission request rejected the constraints (${error.name}); retrying relaxed.`);
        }
    }

    // The original error has to be carried out of the loop: rethrowing a generic 'All attempts failed' error here
    // discarded its name, so every failure - denial, missing device, device in use - produced the same unhelpful
    // 'check your browser settings' guidance.
    result.showGuidance = true;
    result.message = getPermissionErrorMessage(lastError);

    logger.warn(`Media permission request failed: ${lastError?.name}: ${lastError?.message}`);

    return result;
}

/**
 * Aggressively requests permissions by trying multiple techniques to force the dialog.
 * This function attempts to bypass browser permission caching.
 *
 * @param {Object} options - Options for permission request
 * @param {boolean} options.audio - Whether to request audio permission
 * @param {boolean} options.video - Whether to request video permission
 * @returns {Promise<Object>} Result object
 */
export async function forcePermissionRequest({ audio = true, video = true } = {}) {
    const result = {
        success: false,
        showGuidance: false,
        message: '',
        dialogShown: false
    };

    try {
        // Technique 1: Create a unique timestamp-based constraint to potentially bypass cache
        const timestamp = Date.now();
        const uniqueConstraints = {
            audio: audio ? {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,

                // Add a unique property that changes each time
                _timestamp: timestamp
            } : false,
            video: video ? {
                width: { ideal: 640 + (timestamp % 100) }, // Slightly different each time
                height: { ideal: 480 + (timestamp % 50) },
                frameRate: { ideal: 30 },
                _timestamp: timestamp
            } : false
        };

        // Remove the timestamp properties as they're not valid getUserMedia constraints
        if (uniqueConstraints.audio && uniqueConstraints.audio._timestamp) {
            delete uniqueConstraints.audio._timestamp;
        }
        if (uniqueConstraints.video && uniqueConstraints.video._timestamp) {
            delete uniqueConstraints.video._timestamp;
        }

        // Try the request
        const stream = await navigator.mediaDevices.getUserMedia(uniqueConstraints);

        if (stream) {
            // Close the stream immediately
            stream.getTracks().forEach(track => track.stop());

            result.success = true;
            result.dialogShown = true;
            result.message = 'Permissions granted successfully.';

            return result;
        }

    } catch (error) {
        result.dialogShown = true; // Dialog was shown but denied

        if (error.name === 'NotAllowedError') {
            result.message = 'Permission denied. Please allow access to continue.';
        } else if (error.name === 'NotFoundError') {
            result.message = 'No camera or microphone found.';
        } else {
            result.message = 'Unable to access media devices.';
        }
    }

    // Technique 2: If first attempt failed, try with minimal constraints
    try {
        const minimalConstraints = {};

        if (audio) {
            minimalConstraints.audio = true;
        }
        if (video) {
            minimalConstraints.video = true;
        }

        const stream = await navigator.mediaDevices.getUserMedia(minimalConstraints);

        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            result.success = true;
            result.dialogShown = true;
            result.message = 'Permissions granted successfully.';

            return result;
        }

    } catch (error) {
        // Final attempt failed
        result.showGuidance = true;
        result.dialogShown = true;

        if (error.name === 'NotAllowedError') {
            result.message = 'Permission denied. The browser permission dialog was shown but access was denied.';
        } else {
            result.message = 'Unable to access media devices after multiple attempts.';
        }
    }

    return result;
}

/**
 * Shows user-friendly guidance for enabling permissions.
 *
 * @param {Array<string>} devices - The devices the guidance is about, 'audio' and/or 'video'. Defaults to both.
 * @returns {void}
 */
export function showPermissionGuidance(devices = [ 'audio', 'video' ]) {
    // Import at function call to avoid circular dependencies
    const { openPermissionGuidanceDialog } = require('../../device-selection');
    const { store } = require('../../base/redux');

    // Dispatch the permission guidance dialog to show browser-specific instructions
    if (store && store.dispatch && openPermissionGuidanceDialog) {
        store.dispatch(openPermissionGuidanceDialog(devices));
    }
}
